// ============================================================
//  src/perfil.js — Gestión del perfil de usuario
// ============================================================

import { mostrarToast, toggleBoton, mostrarConfirmacion } from './ui.js';
import { httpsCallable } from 'firebase/functions';
import { cargarPerfil, guardarPerfil, eliminarCuentaCompleta,
         usuarioTieneArchivos }                                 from './perfil-datos.js';
import { solicitarAccesoManagedDrive,
         solicitarCambioStorage,
         yaExisteSolicitudManagedDrive,
         cancelarSolicitudManagedDrive }                       from './moderacion.js';
import { cerrarSesion, solicitarPermisosDrive } from './auth.js';
import { auth, functions } from './firebase.js';
import { tienePermisosDrive, limpiarAccessToken, eliminarRecursosDriveCuenta } from './drive.js';
import { mostrarModalAutorizacion } from './modal-autorizar-drive.js';
import { t } from './i18n.js';
import { actualizarTarjetaResumenPerfil, getUltimasEdiciones } from './dashboard.js';
import { actualizarPerfilPublico, eliminarPerfilPublico } from './public-profile.js';
import { getDxccByCode } from './utils/dxcc-utils.js';
import { initInfoTooltip } from './utils/info-tooltip.js';

const TEMAS = { OSCURO: 'oscuro', CLARO: 'claro', AUTO: 'auto' };

// ============================================================
// Tema
// ============================================================

export function aplicarTema(tema) {

    const body = document.body;
    body.classList.remove('tema-oscuro', 'tema-claro');

    let temaEfectivo = tema;
    if (tema === TEMAS.AUTO) {
        const claro = window.matchMedia('(prefers-color-scheme: light)').matches;
        temaEfectivo = claro ? TEMAS.CLARO : TEMAS.OSCURO;
    }

    body.classList.add(`tema-${temaEfectivo}`);

    try {
        localStorage.setItem('theme', temaEfectivo === TEMAS.CLARO ? 'light' : 'dark');
    } catch (_) {}
}

// ============================================================
// Perfil inicial (AMPLIADO)
// ============================================================

function construirPerfilInicial(usuario) {

    return {
        nombre: usuario.displayName ?? '',
        nombreCompleto: usuario.displayName ?? '',
        email: usuario.email ?? '',
        avatar: usuario.photoURL ?? '',

        indicativoOperador: '',
        indicativosEspeciales: [],

        club: '',
        pais: '',
        qth: '',
        gridLocator: '',
        dxcc: '143',

        zonaCQ: '',
        zonaITU: '',

        telefono: '',
        correoElectronico: usuario.email ?? '',
        qrzUrl: '',
        redesSociales: [],

        mostrarTelefonoPublico: true,
        mostrarEmailPublico: true,

        preferencias: {
            tema: TEMAS.OSCURO,
            notificaciones: {
                recordatorios: true,
                resultados: true,
                confirmaciones: false
            }
        },

        driveAutorizado: false,
        fechaRegistro: new Date(),
        ultimoAcceso: new Date()
    };
}

// ============================================================
// Carga perfil
// ============================================================

export async function cargarYAplicarPerfil(usuario) {

    let perfil = await cargarPerfil(usuario.uid);

    if (!perfil) {
        perfil = construirPerfilInicial(usuario);
        await guardarPerfil(usuario.uid, perfil);
    } else {
        await guardarPerfil(usuario.uid, { ultimoAcceso: new Date() });
        perfil = { ...perfil, ultimoAcceso: new Date() };
    }

    perfilActual = perfil;
    aplicarTema(perfil.preferencias?.tema ?? TEMAS.OSCURO);
    actualizarTarjetaResumenPerfil();

    return perfil;
}

let perfilActual = null;

export function getPerfilActual() {
    return perfilActual;
}

export function setPerfilActual(perfil) {
    perfilActual = perfil;
}

export function sincronizarPerfilActualDesdeFirestore(datos = {}) {
    if (!datos || typeof datos !== 'object') return perfilActual;

    perfilActual = { ...(perfilActual ?? {}), ...datos };

    const tema = perfilActual.preferencias?.tema;
    if (tema) aplicarTema(tema);

    const storagePref = perfilActual.storagePreference ?? 'user_drive';

    const radioStorage = document.querySelector(
        `input[name="storage-preference"][value="${storagePref}"]`
    );
    if (radioStorage) radioStorage.checked = true;

    _updateStorageSwitches(storagePref, perfilActual.managedDrivePending === true);

    const driveStatus = document.getElementById('drive-connection-status');
    if (driveStatus) {
        const autorizado = tienePermisosDrive() || (perfilActual.driveAutorizado === true);
        _actualizarEstadoDrive(driveStatus, autorizado);
    }

    if (perfilActual.managedDriveAccess === true) {
        cerrarModalAccesoManaged();
    }

    actualizarTarjetaResumenPerfil();

    return perfilActual;
}

// ============================================================
// Panel perfil
// ============================================================

export async function abrirPanelPerfil() {

    const overlay = document.getElementById('panel-perfil');
    overlay?.classList.add('modal--visible');

    const usuario = auth.currentUser;
    if (!usuario) return;

    if (!perfilActual) {
        perfilActual = await cargarPerfil(usuario.uid) ?? {};
    }

    renderizarPanelPerfil(usuario, perfilActual);
}

export function cerrarPanelPerfil() {
    document.getElementById('panel-perfil')?.classList.remove('modal--visible');
}

// ============================================================
// Render perfil
// ============================================================

function renderizarPanelPerfil(usuario, perfil) {

    const avatar = document.getElementById('perfil-avatar');
    if (avatar && usuario.photoURL) {
        avatar.src = usuario.photoURL;
    }

    setTexto('perfil-nombre', usuario.displayName ?? '—');
    setTexto('perfil-email', usuario.email ?? '—');

    setValor('perfil-indicativo-op', perfil.indicativoOperador ?? '');

    let especiales = perfil.indicativosEspeciales ?? perfil.indicativoEspecial ?? '';
    if (Array.isArray(especiales)) {
        especiales = especiales.join(', ');
    }

    setValor('perfil-indicativos-especiales', especiales);

    setValor('perfil-club', perfil.club ?? '');
    const entidadDxcc = getDxccByCode(perfil.dxcc);
    setValor('perfil-pais',     entidadDxcc ? (perfil.pais ?? '') : '');
    setValor('perfil-dxcc',     entidadDxcc ? String(perfil.dxcc) : '');
    setValor('perfil-zona-cq',  entidadDxcc ? (perfil.zonaCQ  ?? '') : '');
    setValor('perfil-zona-itu', entidadDxcc ? (perfil.zonaITU ?? '') : '');

    setValor('perfil-qth', perfil.qth ?? '');
    setValor('perfil-grid', perfil.gridLocator ?? '');
    setValor('perfil-telefono', perfil.telefono ?? '');
    setValor('perfil-email-contacto', perfil.correoElectronico ?? '');
    setValor('perfil-qrz', perfil.qrzUrl ?? '');

    const chkTelefono = document.getElementById('perfil-mostrar-telefono');
    if (chkTelefono) chkTelefono.checked = perfil.mostrarTelefonoPublico !== false;
    const chkEmail = document.getElementById('perfil-mostrar-email');
    if (chkEmail) chkEmail.checked = perfil.mostrarEmailPublico !== false;

    const notifs = perfil.preferencias?.notificaciones ?? {};
    const chkNotifResultados = document.getElementById('notif-resultados');
    if (chkNotifResultados) chkNotifResultados.checked = notifs.resultados !== false;
    const chkNotifCerts = document.getElementById('notif-certificados');
    if (chkNotifCerts) chkNotifCerts.checked = notifs.certificados !== false;
    const chkNotifResultadosMail = document.getElementById('notif-resultados-mail');
    if (chkNotifResultadosMail) chkNotifResultadosMail.checked = notifs.resultadosMail === true;
    const chkNotifCertsMail = document.getElementById('notif-certificados-mail');
    if (chkNotifCertsMail) chkNotifCertsMail.checked = notifs.certificadosMail === true;

    // Switch general de notificaciones
    const anyNotifActive =
        (notifs.resultados !== false) ||
        (notifs.certificados !== false) ||
        (notifs.resultadosMail === true) ||
        (notifs.certificadosMail === true);
    const notifGeneral = document.getElementById('notif-general');
    if (notifGeneral) notifGeneral.checked = anyNotifActive;
    _actualizarEstadoNotifIndividuales(anyNotifActive);

    // Switch de tema (checked = oscuro)
    const temaSwitch = document.getElementById('perfil-tema-switch');
    if (temaSwitch) temaSwitch.checked = (perfil.preferencias?.tema ?? TEMAS.OSCURO) !== TEMAS.CLARO;

    // Marcar la opción de almacenamiento seleccionada
    const storagePref = perfil.storagePreference ?? 'user_drive';
    const radioStorage = document.querySelector(`input[name="storage-preference"][value="${storagePref}"]`);
    if (radioStorage) radioStorage.checked = true;

    // Switches de almacenamiento
    _updateStorageSwitches(storagePref, perfil.managedDrivePending === true);

    // Estado de conexión Drive dentro de la tarjeta Mi Drive
    const driveStatus = document.getElementById('drive-connection-status');
    if (driveStatus) {
        const autorizado = tienePermisosDrive() || (perfil.driveAutorizado === true);
        _actualizarEstadoDrive(driveStatus, autorizado);
    }

    _initStorageCards();
    _initNotifGeneral();
    _initTemaSwitch();
    window.__sincronizarDxccWidget?.();
}

let _storageCardsInited = false;
let _notifGeneralInited = false;

function _updateStorageSwitches(activeValue, isPending = false) {
    const swUser    = document.getElementById('storage-switch-user');
    const swManaged = document.getElementById('storage-switch-managed');
    const labelManaged = document.querySelector('label[for="storage-switch-managed"]');
    const badge = document.getElementById('storage-cm-pending-badge');

    if (isPending) {
        if (swUser)    swUser.checked    = false;
        if (swManaged) swManaged.checked = false;
        labelManaged?.classList.add('storage-switch--pending');
        if (badge) badge.style.display = '';
    } else {
        if (swUser)    swUser.checked    = activeValue === 'user_drive';
        if (swManaged) swManaged.checked = activeValue === 'managed_drive';
        labelManaged?.classList.remove('storage-switch--pending');
        if (badge) badge.style.display = 'none';
    }
}

function _actualizarEstadoNotifIndividuales(habilitado) {
    const contenedor = document.getElementById('notif-individuales');
    if (!contenedor) return;
    contenedor.querySelectorAll('.pref-row').forEach(row => {
        const input = row.querySelector('.pref-switch__input');
        if (habilitado) {
            row.classList.remove('pref-row--disabled');
            if (input) input.disabled = false;
        } else {
            row.classList.add('pref-row--disabled');
            if (input) input.disabled = true;
        }
    });
}

async function _guardarNotifsInstantaneo() {
    const usuario = auth.currentUser;
    if (!usuario) return;
    const notifs = {
        resultados:       document.getElementById('notif-resultados')?.checked       ?? true,
        certificados:     document.getElementById('notif-certificados')?.checked     ?? true,
        resultadosMail:   document.getElementById('notif-resultados-mail')?.checked  ?? false,
        certificadosMail: document.getElementById('notif-certificados-mail')?.checked ?? false,
    };
    try {
        await guardarPerfil(usuario.uid, { preferencias: { notificaciones: notifs } });
        if (perfilActual) {
            perfilActual.preferencias = perfilActual.preferencias ?? {};
            perfilActual.preferencias.notificaciones = notifs;
        }
    } catch (e) {
        console.error('[Perfil] Error al guardar notificaciones:', e);
    }
}

function _initNotifGeneral() {
    if (_notifGeneralInited) return;
    _notifGeneralInited = true;
    const notifGeneral = document.getElementById('notif-general');
    if (!notifGeneral) return;

    notifGeneral.addEventListener('change', () => {
        _actualizarEstadoNotifIndividuales(notifGeneral.checked);
        if (!notifGeneral.checked) {
            ['notif-resultados', 'notif-certificados', 'notif-resultados-mail', 'notif-certificados-mail'].forEach(id => {
                const el = document.getElementById(id);
                if (el) el.checked = false;
            });
        }
        _guardarNotifsInstantaneo();
    });

    // Guardar al cambiar cualquier switch individual
    ['notif-resultados', 'notif-certificados', 'notif-resultados-mail', 'notif-certificados-mail'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.addEventListener('change', _guardarNotifsInstantaneo);
    });
}

let _temaSwitchInited = false;

function _initTemaSwitch() {
    if (_temaSwitchInited) return;
    _temaSwitchInited = true;
    const temaSwitch = document.getElementById('perfil-tema-switch');
    if (!temaSwitch) return;
    temaSwitch.addEventListener('change', async () => {
        const tema = temaSwitch.checked ? TEMAS.OSCURO : TEMAS.CLARO;
        aplicarTema(tema);
        const usuario = auth.currentUser;
        if (!usuario) return;
        try {
            await guardarPerfil(usuario.uid, { preferencias: { tema } });
            if (perfilActual) {
                perfilActual.preferencias = perfilActual.preferencias ?? {};
                perfilActual.preferencias.tema = tema;
                if (perfilActual.indicativoOperador) {
                    actualizarPerfilPublico(usuario.uid, perfilActual, getUltimasEdiciones()).catch(console.error);
                }
            }
        } catch (e) {
            console.error('[Perfil] Error al guardar tema:', e);
        }
    });
}

// ============================================================
// Drive — estado interactivo
// ============================================================

function _actualizarEstadoDrive(el, autorizado) {
    el.textContent = autorizado
        ? t('perfil.drive.conectado.texto')
        : t('perfil.drive.noconectado.texto');
    el.className = `storage-card__drive-status pref-drive-status storage-card__drive-status--${autorizado ? 'ok' : 'no'}`;
    el.style.cursor = 'pointer';
    el.onclick = autorizado
        ? () => _manejarDesconectarDrive(el)
        : () => _manejarConectarDrive(el);
}

async function _manejarConectarDrive(el) {
    el.style.pointerEvents = 'none';
    try {
        const ok = await solicitarPermisosDrive();
        if (ok) {
            const usuario = auth.currentUser;
            if (usuario) {
                guardarPerfil(usuario.uid, { driveAutorizado: true }).catch(() => {});
                if (perfilActual) perfilActual.driveAutorizado = true;
            }
            mostrarToast(t('toast.drive.conectado'), 'exito');
            _actualizarEstadoDrive(el, true);
        } else {
            mostrarModalAutorizacion(() => {});
        }
    } finally {
        el.style.pointerEvents = '';
    }
}

async function _manejarDesconectarDrive(el) {
    const confirmar = await mostrarConfirmacion(
        t('perfil.drive.desconectar.confirma.titulo'),
        t('perfil.drive.desconectar.confirma.subtitulo'),
        { icono: '🔌', labelConfirmar: t('perfil.drive.desconectar.confirma.btn') }
    );
    if (!confirmar) return;

    el.style.pointerEvents = 'none';
    try {
        await httpsCallable(functions, 'driveDisconnect')({});
        limpiarAccessToken();
        const usuario = auth.currentUser;
        if (usuario) {
            guardarPerfil(usuario.uid, { driveAutorizado: false }).catch(() => {});
            if (perfilActual) perfilActual.driveAutorizado = false;
        }
        mostrarToast(t('toast.drive.desconectado'), 'exito');
        _actualizarEstadoDrive(el, false);
    } catch (error) {
        console.error('[Perfil] Error al desconectar Drive:', error);
        mostrarToast(t('toast.drive.error.desconectar'), 'error');
    } finally {
        el.style.pointerEvents = '';
    }
}

function _revertRadio(valor) {
    const r = document.querySelector(`input[name="storage-preference"][value="${valor}"]`);
    if (r) r.checked = true;
    _updateStorageSwitches(valor);
}

async function _guardarStorageInstantaneo(valor) {
    const usuario = auth.currentUser;
    if (!usuario) return;
    try {
        await guardarPerfil(usuario.uid, { storagePreference: valor });
        if (perfilActual) perfilActual.storagePreference = valor;
    } catch (e) {
        console.error('[Perfil] Error al guardar storage:', e);
        mostrarToast(t('perfil.error.almacenamiento'), 'error');
    }
}

async function _cancelarPendingManaged() {
    const usuario = auth.currentUser;
    if (!usuario) return;
    try {
        await cancelarSolicitudManagedDrive(usuario.uid);
        await guardarPerfil(usuario.uid, { managedDrivePending: false });
        if (perfilActual) perfilActual.managedDrivePending = false;
        _updateStorageSwitches('user_drive', false);
        mostrarToast('Solicitud de Contest Manager cancelada. Mi Drive activado.', 'info');
    } catch (e) {
        console.error('[Perfil] Error al cancelar solicitud CM:', e);
        mostrarToast('Error al cancelar la solicitud.', 'error');
    }
}

function _initStorageCards() {
    if (_storageCardsInited) return;
    _storageCardsInited = true;

    // Tooltips ⓘ
    document.querySelectorAll('.pref-info-btn[data-pref-tooltip]').forEach(btn => {
        const contentEl = document.getElementById(btn.dataset.prefTooltip);
        if (contentEl) initInfoTooltip(btn, contentEl);
    });

    // Switches duales de almacenamiento (mutuamente excluyentes)
    const swUser    = document.getElementById('storage-switch-user');
    const swManaged = document.getElementById('storage-switch-managed');

    function _dispararCambioRadio(targetValue) {
        const radio = document.querySelector(`input[name="storage-preference"][value="${targetValue}"]`);
        if (radio && !radio.checked) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    if (swUser) {
        swUser.addEventListener('change', () => {
            if (swUser.checked) {
                if (swManaged) swManaged.checked = false;
                // Si hay solicitud CM pendiente, cancelarla y mostrar Mi Drive activo
                if (perfilActual?.managedDrivePending === true) {
                    _cancelarPendingManaged();
                    return;
                }
                _dispararCambioRadio('user_drive');
            } else {
                // apagar user_drive → activar managed_drive
                if (swManaged) swManaged.checked = true;
                _dispararCambioRadio('managed_drive');
            }
        });
    }
    if (swManaged) {
        swManaged.addEventListener('change', () => {
            if (swManaged.checked) {
                // Si ya está pendiente, no hacer nada (el radio change lo manejará)
                if (swUser) swUser.checked = false;
                _dispararCambioRadio('managed_drive');
            } else {
                // apagar managed_drive → activar user_drive
                if (swUser) swUser.checked = true;
                _dispararCambioRadio('user_drive');
            }
        });
    }

    // Política de cambio de storage (lógica de validación existente)
    document.querySelectorAll('input[name="storage-preference"]').forEach(radio => {
        radio.addEventListener('change', async (e) => {
            if (!e.target.checked) return;

            const to   = e.target.value;
            const from = perfilActual?.storagePreference ?? 'user_drive';

            // Si ya hay solicitud pendiente y el usuario vuelve a tocar CM: mostrar estado pendiente
            if (to === 'managed_drive' && perfilActual?.managedDrivePending === true) {
                _revertRadio(from);
                _updateStorageSwitches(from, true);
                mostrarToast('Tu solicitud de Contest Manager está pendiente de aprobación.', 'info');
                return;
            }

            if (from === to) return;

            // 1. Usuario ya tiene archivos → solicitar cambio (en cualquier dirección)
            const edicionIds = (getUltimasEdiciones() ?? []).map(ed => ed.id);
            if (edicionIds.length > 0) {
                const tieneArchivos = await usuarioTieneArchivos(edicionIds);
                if (tieneArchivos) {
                    _revertRadio(from);
                    abrirModalCambioStorage(from, to);
                    return;
                }
            }

            // 2. Quiere managed_drive pero no tiene acceso aprobado
            if (to === 'managed_drive' && !perfilActual?.managedDriveAccess) {
                _revertRadio(from);
                try {
                    const yaSolicito = await yaExisteSolicitudManagedDrive(auth.currentUser?.uid);
                    if (yaSolicito) {
                        // Solicitud existente en Firestore — sincronizar estado local
                        if (perfilActual) perfilActual.managedDrivePending = true;
                        _updateStorageSwitches(from, true);
                        mostrarToast('Tu solicitud de Contest Manager está pendiente de aprobación.', 'info');
                    } else {
                        abrirModalAccesoManaged();
                    }
                } catch {
                    abrirModalAccesoManaged();
                }
                return;
            }

            // 3. Sin restricciones — guardar instantáneo
            await _guardarStorageInstantaneo(to);
        });
    });
}

// ============================================================
// Helpers de z-index para modales durante el onboarding
// ============================================================

const Z_SOBRE_ONBOARDING = '9200';

// Guarda { parent, sibling } por id de modal mientras está elevado.
const _modalOriginalPos = new Map();

function _elevarModalOnboarding(id) {
    const el = document.getElementById(id);
    if (!el) return;
    // Ya está elevado: no mover de nuevo
    if (_modalOriginalPos.has(id)) return;
    if (!document.body.classList.contains('onboarding-activo')) return;

    const parent  = el.parentNode;
    const sibling = el.nextSibling;

    // Solo mover si no es ya el último hijo directo de body (evita movimiento innecesario)
    const yaEsUltimoEnBody = parent === document.body && sibling === null;
    if (!yaEsUltimoEnBody) {
        _modalOriginalPos.set(id, { parent, sibling });
        document.body.appendChild(el);
    }
    el.style.zIndex = Z_SOBRE_ONBOARDING;
}

function _restaurarModalOnboarding(id) {
    const el = document.getElementById(id);
    if (!el) return;
    el.style.zIndex = '';

    const pos = _modalOriginalPos.get(id);
    if (!pos) return;
    _modalOriginalPos.delete(id);

    // Restaurar posición original: insertar antes del siguiente hermano guardado
    if (pos.parent && pos.parent.isConnected) {
        pos.parent.insertBefore(el, pos.sibling ?? null);
    }
}

// ============================================================
// Modal: solicitar acceso a managed_drive
// ============================================================

export function abrirModalAccesoManaged() {
    _elevarModalOnboarding('modal-acceso-managed');
    document.getElementById('modal-acceso-managed')?.classList.add('modal--visible');
}

export function cerrarModalAccesoManaged() {
    document.getElementById('modal-acceso-managed')?.classList.remove('modal--visible');
    _restaurarModalOnboarding('modal-acceso-managed');
}

export async function manejarSolicitarAccesoManaged() {
    const usuario = auth.currentUser;
    if (!usuario) return;

    toggleBoton('btn-enviar-acceso-managed', false, t('perfil.enviando'));
    try {
        await solicitarAccesoManagedDrive(usuario.uid, usuario.displayName, usuario.email);
        // Marcar pending en el doc del usuario para persistir el estado entre sesiones
        await guardarPerfil(usuario.uid, { managedDrivePending: true });
        if (perfilActual) perfilActual.managedDrivePending = true;
        _updateStorageSwitches(perfilActual?.storagePreference ?? 'user_drive', true);
        cerrarModalAccesoManaged();
        mostrarToast(t('perfil.acceso-managed.enviado'), 'exito');
    } catch (err) {
        console.error('[Perfil] Error al solicitar managed_drive:', err);
        mostrarToast(t('perfil.acceso-managed.error'), 'error');
    } finally {
        toggleBoton('btn-enviar-acceso-managed', true, t('perfil.acceso-managed.btn'));
    }
}

// ============================================================
// Modal: solicitar cambio de sistema de storage
// ============================================================

function _getStorageLabel(key) {
    if (key === 'user_drive')    return t('perfil.storage.labels.user');
    if (key === 'managed_drive') return t('perfil.storage.labels.managed');
    return key;
}

let _cambioStoragePendiente = { from: null, to: null };

export function abrirModalCambioStorage(from, to) {
    _cambioStoragePendiente = { from, to };
    const desc = document.getElementById('cambio-storage-descripcion');
    if (desc) {
        desc.textContent = t('perfil.storage.cambio.descripcion', {
            from: _getStorageLabel(from),
            to:   _getStorageLabel(to),
        });
    }
    _elevarModalOnboarding('modal-cambio-storage');
    document.getElementById('modal-cambio-storage')?.classList.add('modal--visible');
}

export function cerrarModalCambioStorage() {
    document.getElementById('modal-cambio-storage')?.classList.remove('modal--visible');
    _restaurarModalOnboarding('modal-cambio-storage');
    _cambioStoragePendiente = { from: null, to: null };
}

export async function manejarSolicitarCambioStorage() {
    const usuario = auth.currentUser;
    if (!usuario) return;

    const { from, to } = _cambioStoragePendiente;
    if (!from || !to) return;

    toggleBoton('btn-enviar-cambio-storage', false, t('perfil.enviando'));
    try {
        await solicitarCambioStorage(usuario.uid, from, to);
        cerrarModalCambioStorage();
        mostrarToast(t('perfil.cambio-storage.enviado'), 'exito');
    } catch (err) {
        console.error('[Perfil] Error al solicitar cambio de storage:', err);
        mostrarToast(t('perfil.acceso-managed.error'), 'error');
    } finally {
        toggleBoton('btn-enviar-cambio-storage', true, t('perfil.cambio-storage.btn'));
    }
}

// ============================================================
// Guardar perfil
// ============================================================

export async function manejarGuardarPerfil(evento) {

    evento.preventDefault();

    const usuario = auth.currentUser;
    if (!usuario) return;

    const temaSwitch        = document.getElementById('perfil-tema-switch');
    const tema              = temaSwitch?.checked ? TEMAS.OSCURO : TEMAS.CLARO;
    const storagePreference = document.querySelector('input[name="storage-preference"]:checked')?.value ?? 'user_drive';

    const indicativosEspecialesTexto =
        getValue('perfil-indicativos-especiales');

    const indicativosEspeciales = indicativosEspecialesTexto
        .split(',')
        .map(x => x.trim().toUpperCase())
        .filter(x => x.length > 0);

    const datosActualizados = {

        indicativoOperador: getValue('perfil-indicativo-op').toUpperCase(),

        indicativosEspeciales,

        club: getValue('perfil-club'),

        zonaCQ: getValue('perfil-zona-cq'),
        zonaITU: getValue('perfil-zona-itu'),

        pais: getValue('perfil-pais'),
        qth: getValue('perfil-qth'),
        gridLocator: normalizeGrid(getValue('perfil-grid')),
        dxcc: getValue('perfil-dxcc'),
        telefono: getValue('perfil-telefono'),
        correoElectronico: getValue('perfil-email-contacto'),
        qrzUrl: getValue('perfil-qrz'),

        mostrarTelefonoPublico: getChecked('perfil-mostrar-telefono'),
        mostrarEmailPublico: getChecked('perfil-mostrar-email'),

        storagePreference,

        preferencias: {
            tema,
            notificaciones: {
                recordatorios:    getChecked('notif-recordatorios'),
                resultados:       getChecked('notif-resultados'),
                certificados:     getChecked('notif-certificados'),
                resultadosMail:   getChecked('notif-resultados-mail'),
                certificadosMail: getChecked('notif-certificados-mail'),
            }
        },

        ultimoAcceso: new Date()
    };

    const callsignAnterior = (perfilActual?.indicativoOperador ?? '').trim().toUpperCase();
    toggleBoton('btn-guardar-perfil', false, t('common.guardando'));

    try {

        await guardarPerfil(usuario.uid, datosActualizados);

        perfilActual = { ...perfilActual, ...datosActualizados };

        aplicarTema(tema);

        const elIndicativo = document.getElementById('navbar-indicativo');

        if (elIndicativo && datosActualizados.indicativoOperador) {
            elIndicativo.textContent = datosActualizados.indicativoOperador;
        }

        actualizarTarjetaResumenPerfil();
        actualizarPerfilPublico(usuario.uid, perfilActual, getUltimasEdiciones(), callsignAnterior).catch(console.error);
        mostrarToast(t('toast.perfil.guardado'), 'exito');
        cerrarPanelPerfil();

    } catch (error) {

        console.error('[Perfil] Error al guardar', error);
        mostrarToast(t('toast.perfil.error'), 'error');

    } finally {

        toggleBoton('btn-guardar-perfil', true);
    }
}

// ============================================================
// Eliminación cuenta
// ============================================================

let eliminandoCuenta = false;
let confirmacion1Lista = false;

export function abrirModalEliminarCuenta() {

    confirmacion1Lista = false;
    actualizarEstadoEliminar();
    resetearEstadoProgreso();

    const modal = document.getElementById('modal-eliminar-cuenta');
    modal?.classList.add('modal--visible');

    const campo = document.getElementById('campo-confirmar-eliminar');
    if (campo) campo.value = '';

    const subtitulo = modal?.querySelector('.confirmacion-subtitulo');

    if (subtitulo) {
        if (tienePermisosDrive()) {
            subtitulo.textContent = t('perfil.cuenta.subtitulo-drive');
        } else {
            subtitulo.textContent = t('perfil.cuenta.subtitulo-nodrive');
        }
    }
}

export function cerrarModalEliminarCuenta() {

    document.getElementById('modal-eliminar-cuenta')
        ?.classList.remove('modal--visible');

    confirmacion1Lista = false;
    resetearEstadoProgreso();
}

export function verificarConfirmacionEliminar() {

    const campo = document.getElementById('campo-confirmar-eliminar');

    confirmacion1Lista =
        campo?.value.trim().toUpperCase() ===
        t('cuentaelim.palabra').toUpperCase();

    actualizarEstadoEliminar();
}

function actualizarEstadoEliminar() {

    const btn = document.getElementById('btn-confirmar-eliminar-cuenta');

    if (btn) btn.disabled = !confirmacion1Lista;
}

export async function manejarEliminarCuenta() {

    const usuario = auth.currentUser;

    if (!usuario || !confirmacion1Lista || eliminandoCuenta) return;

    eliminandoCuenta = true;

    const callsignPublico = (perfilActual?.indicativoOperador ?? '').trim().toUpperCase();
    toggleBoton('btn-confirmar-eliminar-cuenta', false, t('perfil.eliminando'));
    mostrarProgreso(2, t('perfil.cuenta.progreso.preparando'));

    let resultadoFirestore = null;
    let resultadoDrive = {
        omitido: false,
        razon: '',
        totalArchivos: 0,
        totalCarpetas: 0,
        eliminadosArchivos: 0,
        eliminadasCarpetas: 0,
        errores: []
    };

    try {

        if (callsignPublico) await eliminarPerfilPublico(callsignPublico).catch(() => {});

        resultadoFirestore = await eliminarCuentaCompleta(usuario.uid, (porcentaje, mensaje) => {
            const pct = Math.max(2, Math.min(72, porcentaje));
            mostrarProgreso(pct, mensaje);
        });

        const totalRecursosDrive =
            (resultadoFirestore?.drive?.archivoIds?.length ?? 0) +
            (resultadoFirestore?.drive?.folderIds?.length ?? 0);

        if (totalRecursosDrive > 0) {

            if (tienePermisosDrive()) {

                resultadoDrive = await eliminarRecursosDriveCuenta(
                    resultadoFirestore.drive,
                    (porcentaje, mensaje) => {
                        const pct = 72 + Math.round((porcentaje / 100) * 20);
                        mostrarProgreso(pct, mensaje);
                    }
                );

            } else {

                resultadoDrive = {
                    omitido: true,
                    razon: 'sin_autorizacion',
                    totalArchivos: resultadoFirestore.drive.archivoIds.length,
                    totalCarpetas: resultadoFirestore.drive.folderIds.length,
                    eliminadosArchivos: 0,
                    eliminadasCarpetas: 0,
                    errores: []
                };

                mostrarProgreso(88, t('perfil.cuenta.progreso.drive-sin-auth'));
            }

        } else {
            mostrarProgreso(88, t('perfil.cuenta.progreso.drive-sin-recursos'));
        }

        mostrarProgreso(95, t('perfil.cuenta.progreso.eliminando-acceso'));

        await usuario.delete();

        limpiarAccessToken();

        cerrarModalEliminarCuenta();
        cerrarPanelPerfil();

        await cerrarSesion({ silencioso: true });

        if (resultadoDrive.omitido) {
            mostrarToast(t('perfil.cuenta.eliminada-drive-omitido'), 'info', 7000);
        } else if ((resultadoDrive.errores?.length ?? 0) > 0) {
            mostrarToast(t('perfil.cuenta.eliminada-drive-errores'), 'info', 7000);
        } else {
            mostrarToast(t('toast.cuenta.eliminada'), 'info');
        }

    } catch (error) {

        console.error('[Perfil] Error al eliminar cuenta:', error);

        if (error?.code === 'auth/requires-recent-login') {
            mostrarToast(t('perfil.cuenta.relogin-requerido'), 'error', 8000);
        } else {
            mostrarToast(t('toast.cuenta.error'), 'error');
        }

        toggleBoton('btn-confirmar-eliminar-cuenta', true);

    } finally {

        eliminandoCuenta = false;
    }
}

// ============================================================
// Progreso eliminación
// ============================================================

function mostrarProgreso(porcentaje, texto) {

    const contenedor = document.getElementById('contenedor-progreso');
    const barra = document.getElementById('progreso-eliminacion-barra');
    const textoEl = document.getElementById('progreso-eliminacion-texto');

    if (contenedor) contenedor.style.display = 'block';
    if (barra) barra.style.width = `${Math.max(0, Math.min(100, porcentaje))}%`;
    if (textoEl) textoEl.textContent = texto;
}

function resetearEstadoProgreso() {

    const contenedor = document.getElementById('contenedor-progreso');
    const barra = document.getElementById('progreso-eliminacion-barra');
    const textoEl = document.getElementById('progreso-eliminacion-texto');

    if (contenedor) contenedor.style.display = 'none';
    if (barra) barra.style.width = '0%';
    if (textoEl) textoEl.textContent = '';
}

// ============================================================
// Helpers
// ============================================================

function normalizeGrid(raw) {
    const s = String(raw ?? '').trim();
    if (s.length !== 6) return '';
    const n = s.slice(0, 2).toUpperCase() + s.slice(2, 4) + s.slice(4, 6).toLowerCase();
    return /^[A-Z]{2}[0-9]{2}[a-z]{2}$/.test(n) ? n : '';
}

const setTexto = (id, v) => {
    const el = document.getElementById(id);
    if (el) el.textContent = v;
};

const setValor = (id, v) => {
    const el = document.getElementById(id);
    if (el) el.value = v;
};

const getValue = id =>
    document.getElementById(id)?.value.trim() ?? '';

const getChecked = id =>
    document.getElementById(id)?.checked ?? false;